import React, { Component } from "react";
import {
  Card, CardBody, Table, Badge, Row, Col, Input, Button,
  Modal, ModalHeader, ModalBody,
} from "reactstrap";
import AiAdminLayout from "./layout";
import { fetchApi } from "./api";

// Pricing per 1M tokens (approximate)
const PRICING = {
  gemini: { input: 0.075, output: 0.30 },    // Gemini 2.5 Flash
  openai: { input: 0.15, output: 0.60 },      // GPT-4o-mini
};

function estimateCost(inputTokens, outputTokens, provider = "gemini") {
  const p = PRICING[provider] || PRICING.gemini;
  const cost = (inputTokens / 1_000_000) * p.input + (outputTokens / 1_000_000) * p.output;
  return cost;
}

function formatCost(cost) {
  if (cost < 0.01) return "<$0.01";
  return "$" + cost.toFixed(2);
}

class AiAdminShopUsage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shops: [],
      loading: true,
      search: "",
      // Detail modal
      detailModal: false,
      detailShop: null,
    };
  }

  async componentDidMount() {
    await this.loadShops();
  }

  loadShops = async () => {
    this.setState({ loading: true });
    try {
      const shops = await fetchApi("/shop-usage");
      this.setState({ shops, loading: false });
    } catch (err) {
      console.error("Shop usage load error:", err);
      this.setState({ loading: false });
    }
  };

  get filteredShops() {
    const { shops, search } = this.state;
    if (!search.trim()) return shops;
    const q = search.trim().toLowerCase();
    return shops.filter(
      (s) => String(s.shop_id).includes(q) || (s.shop_name || "").toLowerCase().includes(q)
    );
  }

  get totals() {
    const { shops } = this.state;
    return shops.reduce(
      (acc, s) => ({
        input_tokens: acc.input_tokens + (s.input_tokens || 0),
        output_tokens: acc.output_tokens + (s.output_tokens || 0),
        total_tokens: acc.total_tokens + (s.total_tokens || 0),
        conversations: acc.conversations + (s.conversation_count || 0),
        messages: acc.messages + (s.message_count || 0),
        cost: acc.cost + estimateCost(s.input_tokens || 0, s.output_tokens || 0, s.provider),
      }),
      { input_tokens: 0, output_tokens: 0, total_tokens: 0, conversations: 0, messages: 0, cost: 0 }
    );
  }

  renderStatCard(label, value, color) {
    return (
      <Col md={4} lg={2} className="mb-3">
        <Card style={{ borderTop: `3px solid ${color}`, borderRadius: 8 }}>
          <CardBody className="py-2 px-3 text-center">
            <div className="text-muted" style={{ fontSize: 10, textTransform: "uppercase" }}>
              {label}
            </div>
            <div className="font-weight-bold" style={{ fontSize: 16 }}>{value}</div>
          </CardBody>
        </Card>
      </Col>
    );
  }

  render() {
    const { loading, search, detailModal, detailShop } = this.state;
    const shops = this.filteredShops;
    const totals = this.totals;

    return (
      <AiAdminLayout>
        <h4 className="font-weight-bold mb-4">Shop Usage</h4>

        {/* Summary Cards */}
        <Row>
          {this.renderStatCard("Total Shops", shops.length, "#667eea")}
          {this.renderStatCard("Conversations", totals.conversations.toLocaleString(), "#764ba2")}
          {this.renderStatCard("Messages", totals.messages.toLocaleString(), "#4facfe")}
          {this.renderStatCard("Input Tokens", totals.input_tokens.toLocaleString(), "#22c55e")}
          {this.renderStatCard("Output Tokens", totals.output_tokens.toLocaleString(), "#3b82f6")}
          {this.renderStatCard("Est. Cost", formatCost(totals.cost), "#ef4444")}
        </Row>

        <Card style={{ borderRadius: 8 }}>
          <CardBody>
            {/* Search */}
            <Row className="mb-3">
              <Col md={4}>
                <Input
                  bsSize="sm"
                  placeholder="Search by Shop ID or name..."
                  value={search}
                  onChange={(e) => this.setState({ search: e.target.value })}
                />
              </Col>
              <Col md={8} className="text-right">
                <Button size="sm" color="light" onClick={this.loadShops}>
                  <i className="simple-icon-refresh mr-1" /> Refresh
                </Button>
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : shops.length === 0 ? (
              <p className="text-muted text-center py-4">No shop usage data found</p>
            ) : (
              <Table responsive size="sm" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Shop ID</th>
                    <th>Shop Name</th>
                    <th className="text-right">Conversations</th>
                    <th className="text-right">Messages</th>
                    <th className="text-right">Input Tokens</th>
                    <th className="text-right">Output Tokens</th>
                    <th className="text-right">Total Tokens</th>
                    <th className="text-right">Est. Cost</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {shops.map((s) => {
                    const cost = estimateCost(s.input_tokens || 0, s.output_tokens || 0, s.provider);
                    return (
                      <tr key={s.shop_id}>
                        <td>
                          <Badge color="light" className="text-dark font-weight-bold">
                            #{s.shop_id}
                          </Badge>
                        </td>
                        <td>{s.shop_name || "—"}</td>
                        <td className="text-right">{(s.conversation_count || 0).toLocaleString()}</td>
                        <td className="text-right">{(s.message_count || 0).toLocaleString()}</td>
                        <td className="text-right" style={{ color: "#22c55e" }}>
                          {(s.input_tokens || 0).toLocaleString()}
                        </td>
                        <td className="text-right" style={{ color: "#3b82f6" }}>
                          {(s.output_tokens || 0).toLocaleString()}
                        </td>
                        <td className="text-right font-weight-bold">
                          {(s.total_tokens || 0).toLocaleString()}
                        </td>
                        <td className="text-right">
                          <Badge color={cost > 1 ? "danger" : cost > 0.1 ? "warning" : "success"} style={{ fontSize: 10 }}>
                            {formatCost(cost)}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            color="link"
                            size="sm"
                            className="p-0"
                            onClick={() => this.setState({ detailModal: true, detailShop: s })}
                          >
                            <i className="simple-icon-magnifier" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        {/* Pricing Note */}
        <div className="mt-3 text-muted" style={{ fontSize: 11 }}>
          * Cost estimates based on: Gemini 2.5 Flash ($0.075/1M input, $0.30/1M output),
          GPT-4o-mini ($0.15/1M input, $0.60/1M output). Actual billing may vary.
        </div>

        {/* Detail Modal */}
        <Modal isOpen={detailModal} toggle={() => this.setState({ detailModal: false })} size="lg">
          <ModalHeader toggle={() => this.setState({ detailModal: false })}>
            Shop #{detailShop?.shop_id} — Usage Detail
          </ModalHeader>
          <ModalBody>
            {detailShop && (
              <div style={{ fontSize: 13 }}>
                <Row className="mb-3">
                  <Col md={3}><strong>Shop ID:</strong></Col>
                  <Col md={9}>{detailShop.shop_id}</Col>
                </Row>
                <Row className="mb-3">
                  <Col md={3}><strong>Shop Name:</strong></Col>
                  <Col md={9}>{detailShop.shop_name || "—"}</Col>
                </Row>

                <hr />
                <h6 className="font-weight-bold mb-3">Conversations & Messages</h6>
                <Row className="mb-2">
                  <Col md={6}>
                    <Card className="text-center py-3" style={{ borderRadius: 8, backgroundColor: "#f8f9fa" }}>
                      <div className="text-muted" style={{ fontSize: 11 }}>CONVERSATIONS</div>
                      <h4 className="mb-0">{(detailShop.conversation_count || 0).toLocaleString()}</h4>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="text-center py-3" style={{ borderRadius: 8, backgroundColor: "#f8f9fa" }}>
                      <div className="text-muted" style={{ fontSize: 11 }}>MESSAGES</div>
                      <h4 className="mb-0">{(detailShop.message_count || 0).toLocaleString()}</h4>
                    </Card>
                  </Col>
                </Row>

                <hr />
                <h6 className="font-weight-bold mb-3">Token Usage</h6>
                <Row className="mb-2">
                  <Col md={4}>
                    <Card className="text-center py-3" style={{ borderRadius: 8, backgroundColor: "#f0fdf4" }}>
                      <div className="text-muted" style={{ fontSize: 11 }}>INPUT TOKENS</div>
                      <h4 className="mb-0" style={{ color: "#22c55e" }}>
                        {(detailShop.input_tokens || 0).toLocaleString()}
                      </h4>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="text-center py-3" style={{ borderRadius: 8, backgroundColor: "#eff6ff" }}>
                      <div className="text-muted" style={{ fontSize: 11 }}>OUTPUT TOKENS</div>
                      <h4 className="mb-0" style={{ color: "#3b82f6" }}>
                        {(detailShop.output_tokens || 0).toLocaleString()}
                      </h4>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="text-center py-3" style={{ borderRadius: 8, backgroundColor: "#fef2f2" }}>
                      <div className="text-muted" style={{ fontSize: 11 }}>TOTAL TOKENS</div>
                      <h4 className="mb-0" style={{ color: "#ef4444" }}>
                        {(detailShop.total_tokens || 0).toLocaleString()}
                      </h4>
                    </Card>
                  </Col>
                </Row>

                <hr />
                <h6 className="font-weight-bold mb-3">Estimated Cost</h6>
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                  <h3 className="mb-0" style={{ color: "#ef4444" }}>
                    {formatCost(estimateCost(detailShop.input_tokens || 0, detailShop.output_tokens || 0, detailShop.provider))}
                  </h3>
                  <small className="text-muted">
                    Based on {detailShop.provider || "gemini"} pricing
                  </small>
                </div>

                {detailShop.audit_count > 0 && (
                  <>
                    <hr />
                    <Row className="mb-2">
                      <Col md={3}><strong>Audit Logs:</strong></Col>
                      <Col md={9}>{detailShop.audit_count} entries</Col>
                    </Row>
                  </>
                )}
              </div>
            )}
          </ModalBody>
        </Modal>
      </AiAdminLayout>
    );
  }
}

export default AiAdminShopUsage;
